import {
  addStageToCompleted,
  isParallelStagesCompleted,
  removeStageFromActive,
} from 'src/util/loading/parallelStageHelpers';

import {
  LoadingMessageKeys,
  PortfolioShowLoadingActions,
  PortfolioShowLoadingStates,
  StageKeys,
  defaultInitialState,
} from './constants';
import { calculateProgress } from '../helpers/buildLoadingOverlayState';

/**
 * The three data stages that run in parallel after meters are displayed.
 */
const PARALLEL_STAGES = [
  StageKeys.RULES,
  StageKeys.CONSUMED_DATA,
  StageKeys.GENERATED_DATA,
];

/**
 * State machine reducer for the PortfolioShow loading pipeline.
 * This reducer manages the state transitions and context updates for the
 * multi-stage portfolio data fetching process. It tracks multiple concurrent
 * active stages via activeStages and completedStages arrays while maintaining
 * a backward-compatible state field. It is intentionally side-effect free to
 * simplify testing and reasoning about state changes.
 * @param {object} state - Current state of the machine
 * @param {object} action - Action to be performed
 * @param {string} action.type - Type of action to perform
 * @param {number} [action.metersCount] - Meter count for SET_METERS_LOADED
 * @param {number} [action.rulesCount] - Rules count for SET_RULES_LOADED
 * @param {number} [action.batchesLoaded] - Batches loaded for SET_TRADE_SUMMARY_PROGRESS
 * @param {number} [action.batchesTotal] - Total batches for SET_TRADE_SUMMARY_PROGRESS
 * @param {Error} [action.error] - Error for SET_ERROR
 * @returns {object} New state after applying the action
 */
const loadingStateReducer = (state, action) => {
  // Guard: when in a terminal state (CANCELLED or ERROR), only allow actions that
  // explicitly start a new request or reset. This prevents stale async dispatches
  // (e.g. from in-flight promises completing after cancellation) from overriding
  // the terminal state and causing the loading overlay to reappear and get stuck.
  const terminalStates = [
    PortfolioShowLoadingStates.CANCELLED,
    PortfolioShowLoadingStates.ERROR,
  ];
  const allowedInTerminalState = [
    PortfolioShowLoadingActions.RESET,
    PortfolioShowLoadingActions.START_REQUEST,
    PortfolioShowLoadingActions.CANCEL_REQUEST,
    PortfolioShowLoadingActions.HIDE_OVERLAY,
  ];
  if (terminalStates.includes(state.state)
    && !allowedInTerminalState.includes(action.type)) {
    return state;
  }

  switch (action.type) {
    case PortfolioShowLoadingActions.RESET:
      return defaultInitialState();

    case PortfolioShowLoadingActions.START_REQUEST: {
      const activeStages = [StageKeys.PORTFOLIO];
      const completedStages = [];
      return {
        state: PortfolioShowLoadingStates.LOADING_PORTFOLIO,
        activeStages,
        completedStages,
        metadata: { ...defaultInitialState().metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.LOADING_PORTFOLIO,
        },
      };
    }

    case PortfolioShowLoadingActions.SET_PORTFOLIO_LOADED: {
      const activeStages = [StageKeys.METERS];
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.PORTFOLIO);
      return {
        state: PortfolioShowLoadingStates.LOADING_METERS,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.PORTFOLIO_LOADED,
        },
      };
    }

    case PortfolioShowLoadingActions.SET_METERS_LOADED: {
      const activeStages = [];
      const completedStages = addStageToCompleted(
        addStageToCompleted(state.completedStages, StageKeys.PORTFOLIO),
        StageKeys.METERS,
      );
      return {
        state: PortfolioShowLoadingStates.METERS_LOADED,
        activeStages,
        completedStages,
        metadata: {
          ...state.metadata,
          metersCount: action.metersCount ?? state.metadata.metersCount,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.METERS_LOADED,
        },
      };
    }

    case PortfolioShowLoadingActions.METERS_DISPLAYED: {
      const activeStages = [StageKeys.RULES, StageKeys.CONSUMED_DATA, StageKeys.GENERATED_DATA];
      const completedStages = [...(state.completedStages || [])];
      return {
        state: PortfolioShowLoadingStates.LOADING_RULES,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.METERS_LOADED,
        },
      };
    }

    case PortfolioShowLoadingActions.SET_RULES_LOADED: {
      const activeStages = removeStageFromActive(state.activeStages, StageKeys.RULES);
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.RULES);
      const allDataLoaded = isParallelStagesCompleted(completedStages, PARALLEL_STAGES);
      return {
        state: allDataLoaded
          ? PortfolioShowLoadingStates.LOADING_TRADE_SUMMARY
          : PortfolioShowLoadingStates.RULES_LOADED,
        activeStages: allDataLoaded
          ? addStageToCompleted(activeStages, StageKeys.TRADE_SUMMARY)
          : activeStages,
        completedStages,
        metadata: {
          ...state.metadata,
          rulesCount: action.rulesCount ?? state.metadata.rulesCount,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: allDataLoaded
            ? LoadingMessageKeys.TRADE_SUMMARY_PROGRESS
            : LoadingMessageKeys.RULES_LOADED,
        },
      };
    }

    case PortfolioShowLoadingActions.SET_CONSUMED_DATA_LOADED: {
      const activeStages = removeStageFromActive(state.activeStages, StageKeys.CONSUMED_DATA);
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.CONSUMED_DATA);
      const allDataLoaded = isParallelStagesCompleted(completedStages, PARALLEL_STAGES);
      return {
        state: allDataLoaded
          ? PortfolioShowLoadingStates.LOADING_TRADE_SUMMARY
          : state.state,
        activeStages: allDataLoaded
          ? addStageToCompleted(activeStages, StageKeys.TRADE_SUMMARY)
          : activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: allDataLoaded
            ? LoadingMessageKeys.TRADE_SUMMARY_PROGRESS
            : LoadingMessageKeys.CONSUMED_DATA_LOADED,
        },
      };
    }

    case PortfolioShowLoadingActions.SET_GENERATED_DATA_LOADED: {
      const activeStages = removeStageFromActive(state.activeStages, StageKeys.GENERATED_DATA);
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.GENERATED_DATA);
      const allDataLoaded = isParallelStagesCompleted(completedStages, PARALLEL_STAGES);
      return {
        state: allDataLoaded
          ? PortfolioShowLoadingStates.LOADING_TRADE_SUMMARY
          : state.state,
        activeStages: allDataLoaded
          ? addStageToCompleted(activeStages, StageKeys.TRADE_SUMMARY)
          : activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: allDataLoaded
            ? LoadingMessageKeys.TRADE_SUMMARY_PROGRESS
            : LoadingMessageKeys.GENERATED_DATA_LOADED,
        },
      };
    }

    case PortfolioShowLoadingActions.SET_METER_DATA_BATCH_PROGRESS:
      return {
        ...state,
        metadata: {
          ...state.metadata,
          meterBatchesLoaded: action.meterBatchesLoaded ?? state.metadata.meterBatchesLoaded,
          meterBatchesTotal: action.meterBatchesTotal ?? state.metadata.meterBatchesTotal,
        },
        overlay: {
          ...state.overlay,
          progressBarValue: calculateProgress(state.completedStages),
        },
      };

    case PortfolioShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS: {
      // Stage 5 now runs in parallel with Stages 2–4. Add TRADE_SUMMARY to
      // activeStages so the checklist shows a spinner for it immediately,
      // even before the reducer transitions to LOADING_TRADE_SUMMARY.
      const tradeSummaryActive = state.activeStages?.includes(StageKeys.TRADE_SUMMARY)
        ? state.activeStages
        : [...(state.activeStages || []), StageKeys.TRADE_SUMMARY];
      return {
        ...state,
        activeStages: tradeSummaryActive,
        metadata: {
          ...state.metadata,
          batchesLoaded: action.batchesLoaded ?? state.metadata.batchesLoaded,
          batchesTotal: action.batchesTotal ?? state.metadata.batchesTotal,
        },
        overlay: {
          ...state.overlay,
          progressBarValue: calculateProgress(state.completedStages),
          messageKey: LoadingMessageKeys.TRADE_SUMMARY_PROGRESS,
        },
      };
    }

    case PortfolioShowLoadingActions.MARK_FULLY_LOADED: {
      const activeStages = [StageKeys.DATA_PREPARED];
      const allStagesUpToTradeSummary = [
        StageKeys.PORTFOLIO,
        StageKeys.METERS,
        StageKeys.RULES,
        StageKeys.CONSUMED_DATA,
        StageKeys.GENERATED_DATA,
        StageKeys.TRADE_SUMMARY,
      ];
      const completedStages = allStagesUpToTradeSummary.reduce(
        (stages, stage) => addStageToCompleted(stages, stage),
        state.completedStages || [],
      );
      return {
        state: PortfolioShowLoadingStates.PREPARING_DATA,
        activeStages,
        completedStages,
        metadata: {
          ...state.metadata,
          metersCount: action.metersCount ?? state.metadata.metersCount,
          rulesCount: action.rulesCount ?? state.metadata.rulesCount,
          batchesLoaded: action.batchesLoaded ?? state.metadata.batchesLoaded,
          batchesTotal: action.batchesTotal ?? state.metadata.batchesTotal,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.PREPARING_DATA,
        },
      };
    }

    case PortfolioShowLoadingActions.DATA_PREPARED: {
      if (state.state !== PortfolioShowLoadingStates.PREPARING_DATA) {
        return state;
      }
      const activeStages = [StageKeys.DATA_RENDERED];
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.DATA_PREPARED);
      return {
        state: PortfolioShowLoadingStates.RENDERING,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.RENDERING,
        },
      };
    }

    case PortfolioShowLoadingActions.RENDERING_DISPLAYED: {
      if (state.state !== PortfolioShowLoadingStates.RENDERING) {
        return state;
      }
      const activeStages = [];
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.DATA_RENDERED);
      return {
        state: PortfolioShowLoadingStates.COMPLETE,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.COMPLETE,
        },
      };
    }

    case PortfolioShowLoadingActions.HIDE_OVERLAY:
      if (state.state === PortfolioShowLoadingStates.ERROR) {
        return state;
      }
      return {
        ...state,
        state: PortfolioShowLoadingStates.IDLE,
        activeStages: [],
        completedStages: [],
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    case PortfolioShowLoadingActions.SET_ERROR:
      return {
        state: PortfolioShowLoadingStates.ERROR,
        activeStages: [],
        completedStages: [...(state.completedStages || [])],
        metadata: {
          ...state.metadata,
          error: action.error || null,
        },
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    case PortfolioShowLoadingActions.CANCEL_REQUEST:
      // Guard: if already cancelled (e.g. handleCancel dispatches CANCEL_REQUEST
      // then setDatespan triggers effect cleanup which dispatches it again),
      // return the same state to avoid a wasteful re-render.
      if (state.state === PortfolioShowLoadingStates.CANCELLED) {
        return state;
      }
      return {
        state: PortfolioShowLoadingStates.CANCELLED,
        activeStages: [],
        completedStages: [...(state.completedStages || [])],
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    default:
      return state;
  }
};

export default loadingStateReducer;
